import React, { useEffect, useState } from "react";
import { Crown, FlaskConical, Radio, Share2 } from "lucide-react";
import { api } from "@/lib/api";
import { ShareResult } from "./ShareResult";

const isAI = (k) => ["ai_oracle", "claude_oracle", "council"].includes(k);

function Row({ r, rank, max, live, onShare }) {
  return (
    <li data-testid={`scoreboard-row-${r.strategy}`} className="grid grid-cols-12 items-center px-4 sm:px-6 py-3 border-b border-white/5 hover:bg-white/[0.02] transition-colors">
      <div className="col-span-1 font-heading text-xl text-parchment/50 tabular-nums">{rank === 1 && r.picks > 0 ? <Crown className="w-4 h-4 text-oracle" strokeWidth={1.5} /> : rank}</div>
      <div className="col-span-5 sm:col-span-4">
        <div className={`font-body text-sm ${isAI(r.strategy) ? "text-oracle" : "text-parchment"}`}>{r.label}</div>
        <div className="font-body text-[11px] text-parchment/40">
          {r.picks} pick{r.picks === 1 ? "" : "s"} scored{live && r.pending ? ` · ${r.pending} awaiting draw` : ""}
        </div>
      </div>
      <div className="col-span-2 font-body text-sm tabular-nums text-parchment/80" data-testid={`scoreboard-avg-${r.strategy}`}>{r.picks ? r.avg_matches.toFixed(2) : "—"}</div>
      <div className="col-span-2 font-body text-sm tabular-nums text-parchment/80">{r.picks ? `${r.three_plus} · ${r.prizes}` : "—"}</div>
      <div className="col-span-2 sm:col-span-3 flex items-center gap-2">
        <div className="flex-1 h-1.5 bg-white/10 rounded-full overflow-hidden">
          <div className="h-full bg-oracle" style={{ width: `${max ? (r.score / max) * 100 : 0}%` }} />
        </div>
        <span className="font-body text-xs text-oracle tabular-nums w-10 text-right">{r.picks ? r.score.toFixed(0) : "—"}</span>
        {live && r.last && (
          <button data-testid={`scoreboard-share-${r.strategy}`} onClick={() => onShare(r)} title={`Share: ${r.label} hit ${r.last.matches} on ${r.last.date}`} className="p-1.5 rounded text-parchment/40 hover:text-oracle hover:bg-white/5 transition-colors">
            <Share2 className="w-3.5 h-3.5" strokeWidth={1.5} />
          </button>
        )}
      </div>
    </li>
  );
}

export function Scoreboard() {
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("live");
  const [shareRow, setShareRow] = useState(null);

  useEffect(() => {
    api.get("/scoreboard").then((r) => setData(r.data)).catch(() => {});
  }, []);

  if (!data) return null;
  const rows = tab === "live" ? data.live : data.backtest.board;
  const max = Math.max(1, ...rows.map((r) => r.score));
  const leader = rows.find((r) => r.picks > 0);
  const highlight = [...data.live].filter((r) => r.last).sort((a, b) => b.last.matches - a.last.matches || (b.last.date > a.last.date ? 1 : -1))[0];

  return (
    <section id="scoreboard" data-testid="scoreboard" className="px-4 sm:px-8 lg:px-12 py-8 max-w-[1400px] mx-auto">
      <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-4 mb-5">
        <div>
          <div className="label-caps text-oracle">Oracle Scoreboard</div>
          <h2 className="font-heading text-3xl sm:text-4xl mt-1 tracking-tight text-parchment">
            {leader ? <>{leader.label} <span className="italic text-oracle">leads.</span></> : <>Who reads the numbers <span className="italic text-oracle">best?</span></>}
          </h2>
          <p className="font-body text-xs text-parchment/50 mt-1 max-w-xl">
            {tab === "live"
              ? `Every pick generated in the app is scored against the drawing it targeted. Next scoring: ${data.next_draw_date} draw.`
              : `Each math strategy replayed on the ${data.backtest.window} drawings from ${data.backtest.from} to ${data.backtest.to}, using only data available before each draw. AI oracles are live-only.`}
          </p>
        </div>
        <div className="flex rounded-md border border-white/10 overflow-hidden self-start" data-testid="scoreboard-tabs">
          <button data-testid="scoreboard-tab-live" onClick={() => setTab("live")} className={`label-caps px-4 py-2.5 flex items-center gap-2 transition-colors ${tab === "live" ? "bg-oracle text-ink" : "text-parchment/60 hover:text-oracle"}`}><Radio className="w-3 h-3" /> Live record</button>
          <button data-testid="scoreboard-tab-backtest" onClick={() => setTab("backtest")} className={`label-caps px-4 py-2.5 flex items-center gap-2 transition-colors ${tab === "backtest" ? "bg-oracle text-ink" : "text-parchment/60 hover:text-oracle"}`}><FlaskConical className="w-3 h-3" /> Backtest</button>
        </div>
      </div>

      {tab === "live" && highlight && (
        <div data-testid="scoreboard-highlight" className="mb-4 bg-oracle/10 border border-oracle/30 rounded-xl px-5 py-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="font-body text-sm text-parchment">
            <span className="text-oracle font-semibold">{highlight.label}</span> hit <span className="text-oracle font-semibold">{highlight.last.matches} number{highlight.last.matches === 1 ? "" : "s"}</span> on the {highlight.last.date} draw{highlight.last.won ? ` — a ${highlight.last.prize} ticket.` : "."}
          </div>
          <button data-testid="scoreboard-highlight-share" onClick={() => setShareRow(highlight)} className="label-caps inline-flex items-center gap-2 text-ink bg-oracle hover:bg-[#FFC033] rounded-md px-3 py-2 transition-colors self-start">
            <Share2 className="w-3.5 h-3.5" strokeWidth={2} /> Share this result
          </button>
        </div>
      )}
      <div className="bg-surface border border-white/5 rounded-xl overflow-hidden">
        <div className="grid grid-cols-12 label-caps px-4 sm:px-6 py-3 border-b border-white/5">
          <div className="col-span-1">#</div>
          <div className="col-span-5 sm:col-span-4">Oracle</div>
          <div className="col-span-2">Avg hits</div>
          <div className="col-span-2">3+ · Prizes</div>
          <div className="col-span-2 sm:col-span-3 text-right">Score</div>
        </div>
        <ul>{rows.map((r, i) => <Row key={r.strategy} r={r} rank={i + 1} max={max} live={tab === "live"} onShare={setShareRow} />)}</ul>
      </div>
      {shareRow && <ShareResult row={shareRow} open={!!shareRow} onOpenChange={(o) => !o && setShareRow(null)} />}
      <p className="font-body text-[11px] text-parchment/35 mt-3">Score = average numbers matched × 100 + 10 per 3+ hit. Powerball is random — past performance is a record, not a promise.</p>
    </section>
  );
}
